import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { ToothLogo } from "@/components/ToothLogo";
import ProCard from "@/components/ProCard";
import { Skeleton } from "@/components/ui/skeleton";

const resultsCache = new Map();

export default function Results() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [pros, setPros] = useState(null);
  const [insurances, setInsurances] = useState([]);
  const [specColor, setSpecColor] = useState("#ffffff");
  const [coverage, setCoverage] = useState("");
  const [covOpen, setCovOpen] = useState(false);
  const covRef = useRef(null);

  const kind = sp.get("kind") || "doctor";
  const country = sp.get("country") || "EC";
  const specialty = sp.get("specialty") || "";
  const province = sp.get("province") || "";
  const city = sp.get("city") || "";
  const zone = sp.get("zone") || "";

  useEffect(() => {
    api.get(`/insurances?country=${country}`).then((r) => setInsurances(r.data)).catch(() => {});
  }, [country]);

  useEffect(() => {
    if (!specialty) {
      setSpecColor("#ffffff");
      return;
    }
    api
      .get(`/specialties/meta?country=${country}`)
      .then((r) => {
        const match = r.data.find((s) => s.name === specialty);
        setSpecColor(match?.color || "#ffffff");
      })
      .catch(() => {});
  }, [country, specialty]);

  useEffect(() => {
    const h = (e) => covRef.current && !covRef.current.contains(e.target) && setCovOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams({ kind, country });
    if (specialty) params.set("specialty", specialty);
    if (province) params.set("province", province);
    if (city) params.set("city", city);
    if (zone) params.set("zone", zone);
    if (coverage) params.set("insurance", coverage);
    const key = params.toString();
    const cached = resultsCache.get(key);
    // Show cached results instantly (no skeleton) when coming back
    setPros(cached ?? null);
    let active = true;
    api
      .get(`/professionals?${key}`)
      .then((r) => {
        resultsCache.set(key, r.data);
        if (active) setPros(r.data);
      })
      .catch(() => {
        if (active && !cached) setPros([]);
      });
    return () => {
      active = false;
    };
  }, [kind, country, specialty, province, city, zone, coverage]);

  const title = useMemo(() => {
    if (specialty) return specialty.toUpperCase();
    return kind === "clinic" ? "CLÍNICAS" : "ODONTÓLOGOS";
  }, [specialty, kind]);

  const noun = kind === "clinic" ? "clínicas encontradas" : "especialistas encontrados";
  const titleSize = title.length > 18 ? 14 : title.length > 14 ? 17 : title.length > 11 ? 19 : 22;

  return (
    <div className="app-shell flex flex-col" style={{ minHeight: "100dvh", background: "linear-gradient(to bottom, #595959, #DBDCDE)" }}>
      {/* Header */}
      <div className="relative overflow-hidden rounded-b-[33px] pb-3 backdrop-blur-md" style={{ backgroundColor: "#585858", borderBottom: `1px solid ${specColor}` }}>
        <div className="flex items-center gap-3 px-4 pb-3 pt-6">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            aria-label="Volver"
            className="grid h-10 w-10 place-items-center rounded-full active:scale-95"
          >
            <ArrowLeft className="h-6 w-6" style={{ color: "#AEAEAE" }} />
          </button>
          <div className="flex flex-1 items-center justify-end gap-2 pr-4">
            <span className="grid place-items-center rounded-full ring-1 ring-white" style={{ width: 44, height: 26 }}>
              <img src="/assets/tooth-results.png" alt="" className="object-contain" style={{ width: 13, height: 13 }} />
            </span>
            <h1 className="font-brand whitespace-nowrap text-right tracking-wide text-white drop-shadow" style={{ fontSize: titleSize, fontWeight: 300 }}>{title}</h1>
          </div>
        </div>
      </div>

      {/* Sub header */}
      <div className="flex items-center justify-between px-5 py-4">
        <span data-testid="results-count" className="font-medium text-white" style={{ fontSize: 9 }}>
          {pros ? `${pros.length} ${noun}` : "Buscando…"}
        </span>
        <div className="relative" ref={covRef}>
          <button
            data-testid="coverage-filter-btn"
            onClick={() => setCovOpen((o) => !o)}
            className="flex items-center gap-1 font-normal text-white"
            style={{ fontSize: 11 }}
          >
            {coverage || "Atención / Cobertura"}
            <ChevronDown className="h-5 w-5 text-white" />
          </button>
          {covOpen && (
            <div className="no-scrollbar absolute right-0 z-40 mt-2 max-h-64 w-52 overflow-y-auto rounded-2xl border border-black/5 bg-white p-1 shadow-2xl">
              <button
                onClick={() => {
                  setCoverage("");
                  setCovOpen(false);
                }}
                className="block w-full rounded-xl px-4 py-2.5 text-left text-base text-neutral-500 hover:bg-neutral-100"
              >
                Todas
              </button>
              {insurances.map((i) => (
                <button
                  key={i.id}
                  data-testid={`coverage-option-${i.name}`}
                  onClick={() => {
                    setCoverage(i.name);
                    setCovOpen(false);
                  }}
                  className={`block w-full rounded-xl px-4 py-2.5 text-left text-base ${
                    coverage === i.name ? "bg-neutral-100 font-medium" : "hover:bg-neutral-100"
                  }`}
                >
                  {i.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* List */}
      <div className="no-scrollbar flex-1 space-y-5 overflow-y-auto px-4 pb-10">
        {!pros &&
          [0, 1, 2].map((i) => <Skeleton key={i} className="h-[172px] w-full rounded-[28px] bg-white/40" />)}
        {pros && pros.length === 0 && (
          <div className="mt-16 text-center text-neutral-600">
            <p className="font-medium text-white">No encontramos resultados</p>
            <p className="text-sm text-white/70">Prueba con otra ciudad o especialidad.</p>
          </div>
        )}
        {pros && pros.map((p, i) => <ProCard key={p.id} pro={p} index={i} />)}
      </div>
    </div>
  );
}
